import React, { useState, useEffect } from "react";

const G = "#0077B5";
const B = "#E1E9EE";
const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

const SectionLabel = ({ children }) => (
  <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, letterSpacing: "0.2em", color: "#999999", textTransform: "uppercase", marginBottom: 16 }}>{children}</div>
);

const JURISDICTION_LABELS = { ca: "Canada", us: "United States", na: "Both" };

export default function PracticeAreasSection({ user, setUser }) {
  const [areas, setAreas] = useState([]);
  const [profession, setProfession] = useState("");
  const [jurisdiction, setJurisdiction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jurisSaving, setJurisSaving] = useState(false);
  const [showProfSwitch, setShowProfSwitch] = useState(false);
  const [availableProfs, setAvailableProfs] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [showMoreAreas, setShowMoreAreas] = useState(false);

  const plan = (user?.plan || "free").toLowerCase();
  const isPro = plan === "pro" || plan === "pro_team" || plan === "pro_team_member" || plan === "authority_pro";
  const hadPro = !isPro && user?.user_profession && user.user_profession !== "general";

  useEffect(() => {
    if (!isPro) return;
    const st = localStorage.getItem("nectar_st");
    fetch(`${API_BASE}/app/api/user/practice-areas`, {
      headers: { "X-Session-Token": st },
    })
      .then(r => r.json())
      .then(d => {
        setAreas(d.practice_areas || []);
        setProfession(d.profession || "");
        setJurisdiction(d.jurisdiction || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isPro]);

  // Non-Pro users who never had Pro: hide entirely
  if (!isPro && !hadPro) return null;

  // Downgrade state: former Pro user on a lower plan
  if (hadPro && !isPro) {
    const profLabel = {
      lawyer: "Lawyer", accountant: "Accountant", mortgage_broker: "Mortgage Broker",
      insurance_advisor: "Insurance Advisor", real_estate: "Real Estate",
    }[user.user_profession] || user.user_profession;
    const jurisLabel = JURISDICTION_LABELS[user.jurisdiction] || user.jurisdiction || "";
    return (
      <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", opacity: 0.55 }}>
        <SectionLabel>Your field</SectionLabel>
        <div style={{ fontSize: 14, color: "#333", marginBottom: 8 }}>
          <span style={{ fontWeight: 600 }}>{profLabel}</span>
          {jurisLabel && <><span style={{ color: "#999", margin: "0 8px" }}>&middot;</span><span style={{ color: "#666" }}>{jurisLabel}</span></>}
        </div>
        <div style={{ fontSize: 13, color: "#888", lineHeight: 1.6, marginBottom: 12 }}>
          Your Pro plan has ended. Your posts now come from your topics rather than professional content in your field. Renew anytime to get your expert content back.
        </div>
        <button
          onClick={() => window.location.hash = "#/settings/billing"}
          style={{ background: "#0077B5", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}
        >
          Renew Pro
        </button>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 20, color: "#999" }}>Loading...</div>;

  const selectedCount = areas.filter(a => a.selected).length;

  const toggleArea = async (slug) => {
    const area = areas.find(a => a.category_slug === slug);
    if (!area) return;

    // Prevent unchecking the last one
    if (area.selected && selectedCount <= 1) return;

    const newAreas = areas.map(a =>
      a.category_slug === slug ? { ...a, selected: !a.selected } : a
    );
    setAreas(newAreas);

    setSaving(true);
    const st = localStorage.getItem("nectar_st");
    const selectedSlugs = newAreas.filter(a => a.selected).map(a => a.category_slug);
    await fetch(`${API_BASE}/app/api/user/practice-areas`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-Session-Token": st },
      body: JSON.stringify({ selected_slugs: selectedSlugs }),
    });
    setSaving(false);
  };

  const updateJurisdiction = async (val) => {
    setJurisdiction(val);
    setJurisSaving(true);
    const st = localStorage.getItem("nectar_st");
    await fetch(`${API_BASE}/app/api/user/settings`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-Session-Token": st },
      body: JSON.stringify({ jurisdiction: val }),
    });
    if (setUser) {
      setUser(prev => ({ ...prev, jurisdiction: val }));
    }
    setJurisSaving(false);
  };

  const profLabel = {
    lawyer: "Lawyer",
    accountant: "Accountant",
    financial_advisor: "Financial Advisor",
    mortgage_broker: "Mortgage Broker",
    insurance_advisor: "Insurance Advisor (P&C)",
    cybersecurity: "Cybersecurity Professional",
    ai_professional: "AI / Machine Learning Professional",
    real_estate: "Real Estate",
  }[profession] || profession;

  const jurisLabel = JURISDICTION_LABELS[jurisdiction] || jurisdiction || "Not set";

  return (
    <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
      <SectionLabel>Your professional content</SectionLabel>

      <div style={{ fontSize: 13, color: "#666", marginBottom: 12, lineHeight: 1.5 }}>
        You're getting expert content for this field, plus the topics you choose below.
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 14, color: "#333" }}>
          <span style={{ fontWeight: 600 }}>Your field: {profLabel}</span>
          <span style={{ color: "#999", margin: "0 8px" }}>&middot;</span>
          <span style={{ color: "#666" }}>{jurisLabel}</span>
        </div>
        <button
          onClick={async () => {
            const st = localStorage.getItem("nectar_st");
            const r = await fetch(`${API_BASE}/app/api/onboard/config`);
            const d = await r.json();
            setAvailableProfs((d.professions || []).filter(p => p.has_curated && p.id !== profession));
            setShowProfSwitch(true);
          }}
          style={{ background: "none", border: "none", color: G, fontSize: 13, cursor: "pointer", fontWeight: 500 }}
        >Change</button>
      </div>

      {showProfSwitch && (
        <div style={{ background: "#F9FAFB", border: `1px solid ${B}`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 8 }}>Switch profession</div>
          <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 12 }}>
            This will reset your practice areas and delete pending posts.
          </div>
          {availableProfs.length === 0 ? (
            <div style={{ fontSize: 13, color: "#888" }}>No other professions available yet.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {availableProfs.map(p => (
                <button key={p.id} disabled={switching} onClick={async () => {
                  if (!window.confirm(`Switch to ${p.label}? This will reset your practice areas and delete pending posts.`)) return;
                  setSwitching(true);
                  const st = localStorage.getItem("nectar_st");
                  const r = await fetch(`${API_BASE}/app/api/user/switch-profession`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-Session-Token": st },
                    body: JSON.stringify({ profession: p.id }),
                  });
                  if (r.ok) {
                    window.location.reload();
                  } else {
                    const d = await r.json().catch(() => ({}));
                    alert(d.error || "Failed to switch");
                  }
                  setSwitching(false);
                }} style={{
                  padding: "8px 16px", fontSize: 13, borderRadius: 8,
                  border: `1px solid ${B}`, background: "#fff", cursor: "pointer",
                  opacity: switching ? 0.5 : 1,
                }}>{p.label}</button>
              ))}
            </div>
          )}
          <button onClick={() => setShowProfSwitch(false)} style={{ marginTop: 10, background: "none", border: "none", color: "#888", fontSize: 12, cursor: "pointer" }}>Cancel</button>
        </div>
      )}

      {/* Practice areas */}
      <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
        Practice areas — what your professional content covers
        {saving && <span style={{ marginLeft: 8, color: G, fontSize: 12 }}>Saving...</span>}
      </div>

      {(() => {
        const available = areas.filter(a => a.has_coverage || a.selected);
        const unavailable = areas.filter(a => !a.has_coverage && !a.selected);
        return (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: unavailable.length > 0 ? 10 : 20 }}>
              {available.map(area => {
                const isLast = area.selected && selectedCount <= 1;
                return (
                  <label key={area.id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 12px", borderRadius: 8,
                    border: `1px solid ${area.selected ? G : "#E5E7EB"}`,
                    background: area.selected ? "#F0F9FF" : "transparent",
                    cursor: isLast ? "not-allowed" : "pointer",
                    opacity: isLast ? 0.7 : 1,
                  }}>
                    <input type="checkbox" checked={area.selected}
                      onChange={() => toggleArea(area.category_slug)}
                      disabled={isLast} style={{ accentColor: G }} />
                    <span style={{ fontSize: 14, color: area.selected ? "#333" : "#888" }}>{area.label}</span>
                  </label>
                );
              })}
            </div>
            {unavailable.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <button onClick={() => setShowMoreAreas(!showMoreAreas)} style={{
                  background: "none", border: "none", color: "#888", fontSize: 12,
                  cursor: "pointer", padding: "4px 0",
                }}>
                  {showMoreAreas ? "Hide" : `${unavailable.length} more areas`} — coming soon
                </button>
                {showMoreAreas && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
                    {unavailable.map(area => (
                      <div key={area.id} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "6px 12px", borderRadius: 8, opacity: 0.45,
                      }}>
                        <input type="checkbox" checked={false} disabled style={{ accentColor: G }} />
                        <span style={{ fontSize: 13, color: "#999" }}>
                          {area.label}
                          <span style={{ fontSize: 11, marginLeft: 6 }}>No feeds yet</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}

      {selectedCount <= 1 && (
        <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 16 }}>
          At least one practice area must be selected.
        </div>
      )}

      {/* Jurisdiction */}
      <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
        Where do you practice?
        {jurisSaving && <span style={{ marginLeft: 8, color: G, fontSize: 12 }}>Saving...</span>}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        {[
          { value: "ca", label: "Canada" },
          { value: "us", label: "United States" },
          { value: "na", label: "Both" },
        ].map(opt => (
          <label
            key={opt.value}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 14px", borderRadius: 8,
              border: `1px solid ${jurisdiction === opt.value ? G : "#E5E7EB"}`,
              background: jurisdiction === opt.value ? "#F0F9FF" : "transparent",
              cursor: "pointer", fontSize: 14,
              color: jurisdiction === opt.value ? "#333" : "#888",
            }}
          >
            <input
              type="radio"
              name="jurisdiction"
              checked={jurisdiction === opt.value}
              onChange={() => updateJurisdiction(opt.value)}
              style={{ accentColor: G }}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}
